import { useState } from "react";

import {
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { useForm } from "react-hook-form";

import toast from "react-hot-toast";

import authService from "../services/authService";

import styles from "./Login.module.css";


export default function Login({
  onClose,
}) {
  const [error, setError] =
    useState("");

  const [showPassword, setShowPassword] =
    useState(false);

  const [isLoading, setIsLoading] =
    useState(false);

  const navigate = useNavigate();

  const location = useLocation();


  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      login: "",
      password: "",
    },
  });


  /*
  =====================================================
  PASSWORD VISIBILITY
  =====================================================
  */

  const togglePasswordVisibility =
    () => {
      setShowPassword(
        (prev) => !prev
      );
    };


  /*
  =====================================================
  CLOSE POPUP
  =====================================================
  */

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      navigate("/");
    }
  };


  /*
  =====================================================
  LOGIN
  =====================================================
  */

  const onSubmit = async (
    formData
  ) => {
    setError("");

    setIsLoading(true);

    try {
      const data =
        await authService.login({
          login: formData.login,
          password:
            formData.password,
        });


      /*
      -----------------------------------------------
      INVALID RESPONSE
      -----------------------------------------------
      */

      if (
        !data ||
        data.success === false
      ) {
        throw new Error(
          "Invalid credentials"
        );
      }


      /*
      -----------------------------------------------
      ADMIN CHECK
      -----------------------------------------------
      */

      if (
        data.user?.user_role ===
        "admin"
      ) {
        toast.error(
          "Admin not allowed here. Please use Admin Login."
        );

        authService.logout?.();

        setIsLoading(false);

        return;
      }


      /*
      -----------------------------------------------
      SUCCESS
      -----------------------------------------------
      */

      toast.success(
        "Logged in successfully!"
      );


      /*
      IMPORTANT:
      Notify Navbar immediately.

      Without this, Navbar waits until
      refresh/focus to call getCurrentUser().
      */

      window.dispatchEvent(
        new Event("auth-changed")
      );


      /*
      Close popup
      */

      if (onClose) {
        onClose();
      }


      /*
      Navigate after login
      */

      setTimeout(() => {
        setIsLoading(false);

        const redirectPath =
          location.state?.from ||
          "/";

        navigate(
          redirectPath,
          {
            replace: true,
          }
        );
      }, 500);

    } catch (err) {
      console.error(
        "Login error:",
        err
      );

      toast.error(
        err?.response?.data
          ?.message ||
          "Invalid credentials"
      );

      setError(
        "Invalid email/phone number or password."
      );

      setIsLoading(false);
    }
  };


  /*
  =====================================================
  SWITCH TO OTP LOGIN
  =====================================================
  */

  const handleOtpLogin = () => {

    // Close password popup
    if (onClose) {
      onClose();
    }


    // Open OTP popup
    setTimeout(() => {
      window.dispatchEvent(
        new Event(
          "open-login-otp"
        )
      );
    }, 0);
  };


  return (
    <div
      className={styles.overlay}
      onClick={handleClose}
    >

      <div
        className={styles.modal}
        onClick={(e) =>
          e.stopPropagation()
        }
      >

        {/* CLOSE BUTTON */}

        <button
          type="button"
          className={
            styles.closeButton
          }
          onClick={handleClose}
          aria-label="Close"
        >
          ×
        </button>


        {/* LOGO */}

        <div
          className={
            styles.logoContainer
          }
        >
          <Link to="/">
            <img
              src="/vitauralogo.jpg"
              alt="Vitaura Logo"
              className={
                styles.logo
              }
            />
          </Link>
        </div>


        {/* TITLE */}

        <h4
          className={
            styles.title
          }
        >
          Welcome to Vitaura ! 👋
        </h4>


        <p
          className={
            styles.subtitle
          }
        >
          Please sign-in to your account
        </p>


        {/* ERROR */}

        {error && (
          <div className="alert alert-danger">
            {error}
          </div>
        )}


        {/* FORM */}

        <form
          onSubmit={handleSubmit(
            onSubmit
          )}
          className="mb-3"
        >

          {/* EMAIL / PHONE */}

          <div className="mb-3">

            <label className="form-label">
              Email or Phone Number
            </label>

            <input
              type="text"
              className="form-control"
              placeholder="Enter email or phone number"
              {...register(
                "login",
                {
                  required:
                    "Email or phone number is required.",
                }
              )}
            />

            {errors.login && (
              <p className="text-danger mt-1">
                {
                  errors.login
                    .message
                }
              </p>
            )}

          </div>


          {/* PASSWORD */}

          <div className="mb-3">

            <div className="d-flex justify-content-between">

              <label className="form-label">
                Password
              </label>

              <Link
                to="/forgot-password"
                style={{
                  color:
                    "#FB6C22",
                  textDecoration:
                    "none",
                }}
                onClick={() => {
                  if (onClose) {
                    onClose();
                  }
                }}
              >
                <small>
                  Forgot Password?
                </small>
              </Link>

            </div>


            <div className="input-group input-group-merge">

              <input
                type={
                  showPassword
                    ? "text"
                    : "password"
                }
                className="form-control"
                placeholder="••••••••"
                {...register(
                  "password",
                  {
                    required:
                      "Password is required.",
                  }
                )}
              />


              <span
                className="input-group-text"
                style={{
                  cursor:
                    "pointer",
                }}
                onClick={
                  togglePasswordVisibility
                }
              >
                <i
                  className={
                    showPassword
                      ? "bx bx-show"
                      : "bx bx-hide"
                  }
                />
              </span>

            </div>


            {errors.password && (
              <p className="text-danger mt-1">
                {
                  errors.password
                    .message
                }
              </p>
            )}

          </div>


          {/* SIGN IN */}

          <div className="mb-3">

            <button
              className="btn d-grid w-100"
              type="submit"
              disabled={
                isLoading
              }
              style={{
                backgroundColor:
                  "#FB6C22",
                color: "#fff",
                border:
                  "none",
                height:
                  "45px",
              }}
            >

              {isLoading ? (
                <span className="spinner-border spinner-border-sm" />
              ) : (
                "Sign in"
              )}

            </button>

          </div>

        </form>


        {/* REGISTER */}

        <p className="text-center mb-3">

          <span>
            New User?{" "}
          </span>

        <button
  type="button"
  style={{
    color: "#FB6C22",
    textDecoration: "none",
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
  }}
  onClick={() => {
    // Close Login popup
    if (onClose) {
      onClose();
    }

    // Open Login OTP popup
    setTimeout(() => {
      window.dispatchEvent(
        new Event("open-login-otp")
      );
    }, 0);
  }}
>
  Create an account
</button>

        </p>


        {/* OTP LOGIN */}

        {/* <div className="text-center">

          <button
            type="button"
            onClick={
              handleOtpLogin
            }
            style={{
              color:
                "#FB6C22",
              background:
                "none",
              border:
                "none",
              padding: 0,
              cursor:
                "pointer",
              fontSize:
                "14px",
            }}
          >
            Login with OTP
          </button>

        </div> */}

      </div>

    </div>
  );
}